import { NavLink, useLocation } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import {
  AlertTriangle, BarChart2, Bell, BookOpen, Calendar, CalendarDays, CheckSquare, ChevronDown, ClipboardList,
  Clock, DollarSign, FileText, FolderOpen, Gem, Grid3x3, Hammer, HardHat, Home, Inbox,
  KeyRound, LayoutDashboard, Map, MessageCircle, Package, PlaneTakeoff, RefreshCw, Search,
  Settings, ShieldCheck, ShoppingCart, Sparkles, Star, Sun, Tag, Users,
  Volume2, Wallet, Wrench, Zap,
} from 'lucide-react';
import { CommandPalette, type PaletteLink } from './CommandPalette';

/** Maps emoji icon strings → Lucide SVG components for the sidebar. */
type LucideComp = React.ComponentType<{ size?: number; className?: string; 'aria-hidden'?: boolean | 'true' | 'false' }>;
const ICON_COMPONENTS: Record<string, LucideComp> = {
  '🚨': AlertTriangle,
  '📊': LayoutDashboard,
  '☀️': Sun,
  '🔔': Bell,
  '🟩': Grid3x3,
  '✈️': PlaneTakeoff,
  '📋': ClipboardList,
  '✓':  CheckSquare,
  '🗂️': FolderOpen,
  '📨': Inbox,
  '⚡': Zap,
  '👥': Users,
  '💎': Gem,
  '💬': MessageCircle,
  '🕐': Clock,
  '⭐': Star,
  '🏷️': Tag,
  '🏠': Home,
  '📅': Calendar,
  '🔑': KeyRound,
  '🔊': Volume2,
  '🔐': ShieldCheck,
  '🔄': RefreshCw,
  '🧹': Sparkles,
  '👷': HardHat,
  '🗓️': CalendarDays,
  '$':  DollarSign,
  '🔩': Wrench,
  '🔧': Settings,
  '🔍': Search,
  '📦': Package,
  '🛠️': Hammer,
  '📝': FileText,
  '🛒': ShoppingCart,
  '💰': Wallet,
  '🗺️': Map,
  '📖': BookOpen,
  '📈': BarChart2,
  '🔁': RefreshCw,
};

/** Render a nav icon: Lucide SVG when mapped, emoji text as fallback. */
function NavIcon({ icon, className = 'w-4 h-4 shrink-0' }: { icon: string; className?: string }) {
  const Comp = ICON_COMPONENTS[icon];
  if (Comp) return <Comp className={className} aria-hidden="true" />;
  return <span className="text-base w-5 text-center shrink-0" aria-hidden="true">{icon}</span>;
}

// ---------------------------------------------------------------------------
// Navigation data — grouped sections
// ---------------------------------------------------------------------------

interface NavItem {
  name: string;
  href: string;
  icon: string;
  badgeKey?: string;
}

interface NavSection {
  name: string;
  icon: string;
  items: NavItem[];
}

const ALL_NAV_SECTIONS: NavSection[] = [
  {
    name: 'Overview',
    icon: '📊',
    items: [
      { name: 'Action Required',  href: '/app/action-required', icon: '🚨', badgeKey: 'actionRequired' },
      { name: 'Dashboard',       href: '/app/dashboard',    icon: '📊' },
      { name: "Today's Activity",href: '/app/today',        icon: '☀️' },
      { name: 'Activity Feed',   href: '/app/activity',     icon: '🔔', badgeKey: 'activity' },
      { name: 'Arrival Readiness', href: '/app/checkin-readiness',              icon: '✈️' },
      { name: 'Check-in Forms',    href: '/app/checkin-readiness/submissions',  icon: '📋' },
      { name: 'Notepad',           href: '/app/notepad',           icon: '📝' },
      { name: 'Settings',          href: '/app/settings',          icon: '⚙️' },
      { name: 'Notification Prefs', href: '/app/settings/notifications', icon: '🔔' },
      { name: 'Owner Tasks',     href: '/app/owner-tasks',  icon: '📋' },
      { name: 'Task Analytics',  href: '/app/owner-tasks/analytics', icon: '📈' },
      { name: 'Tasks',           href: '/app/tasks',        icon: '✓'  },
    ],
  },
  {
    name: 'Bookings & Guests',
    icon: '🗂️',
    items: [
      { name: 'Booking Requests', href: '/app/booking-requests',         icon: '📨', badgeKey: 'bookingRequests' },
      { name: 'Auto-Routing',       href: '/app/booking-requests/routing',        icon: '⚡' },
      { name: 'Cancellation Report', href: '/app/booking-requests/cancellations', icon: '📉' },
      { name: 'Booking Funnel',     href: '/app/booking-requests/funnel',        icon: '📊' },
      { name: 'Bookings',         href: '/app/bookings',                 icon: '🗂️' },
      { name: 'Profitability',    href: '/app/bookings/profitability',   icon: '💹' },
      { name: 'Cancellation Risk', href: '/app/bookings/cancellation-risk', icon: '⚠️' },
      { name: 'Pipeline',        href: '/app/bookings/pipeline',        icon: '🔀' },
      { name: 'Govt Travel',     href: '/app/bookings/government',      icon: '🎖️' },
      { name: 'Long-Term Stays', href: '/app/bookings/long-term',       icon: '🏠' },
      { name: 'Date Changes',    href: '/app/bookings/date-changes',    icon: '📅' },
      { name: 'Guest Cases',     href: '/app/cases',                    icon: '🗃️' },
      { name: 'Guests',           href: '/app/guests',                   icon: '👥' },
      { name: 'Upcoming Arrivals',  href: '/app/guests/arrivals',          icon: '🛬' },
      { name: 'Guest Intelligence', href: '/app/guests/intelligence',      icon: '🧠' },
      { name: 'Guest Lifetime Value', href: '/app/guests/lifetime-value',icon: '💎' },
      { name: 'Guest Retention',      href: '/app/guests/retention',      icon: '🔄' },
      { name: 'Rebooking Campaigns', href: '/app/guests/rebooking',      icon: '📣' },
      { name: 'Rebooking Analytics', href: '/app/guests/rebooking/analytics', icon: '📈' },
      { name: 'Email Suppressions', href: '/app/guests/suppressions',       icon: '🚫' },
      { name: 'Guest Watchlist',    href: '/app/guests/watchlist',          icon: '⭐' },
      { name: 'Availability Waitlist', href: '/app/guests/waitlist',        icon: '📋' },
      { name: 'Unified Inbox',     href: '/app/messages/unified',         icon: '📬', badgeKey: 'messages'      },
      { name: 'Messages',         href: '/app/messages',                 icon: '💬' },
      { name: 'Guest Conversations', href: '/app/guest-conversations',   icon: '🗨️', badgeKey: 'guestConvs'   },
      { name: 'Airbnb Inbox',      href: '/app/messages/airbnb',          icon: '🏠' },
      { name: 'VRBO Inbox',        href: '/app/messages/vrbo',            icon: '🏡' },
      { name: 'Booking.com Inbox', href: '/app/messages/booking-com',     icon: '🏨' },
      { name: 'Message Sequences', href: '/app/messages/sequences',       icon: '📋' },
      { name: 'Message Delivery',  href: '/app/messages/delivery',        icon: '📬' },
      { name: 'Guest Requests',   href: '/app/guest-requests',           icon: '🕐', badgeKey: 'guestRequests' },
      { name: 'Inquiries',       href: '/app/inquiries',                icon: '📩', badgeKey: 'inquiries'      },
      { name: 'Reviews',          href: '/app/reviews',                  icon: '⭐' },
      { name: 'Review Analytics', href: '/app/reviews/analytics',        icon: '📈' },
      { name: 'Review Drafts',   href: '/app/reviews/drafts',           icon: '✏️' },
      { name: 'Airbnb Reviews',  href: '/app/reviews/airbnb',           icon: '🌟' },
      { name: 'Review Sentiment', href: '/app/reviews/sentiment',        icon: '🔍' },
      { name: 'Guest Surveys',   href: '/app/surveys',                  icon: '📝' },
      { name: 'Discount Codes',       href: '/app/discount-codes',            icon: '🏷️' },
      { name: 'Discount Analytics',   href: '/app/discount-codes/analytics',  icon: '📈' },
    ],
  },
  {
    name: 'Properties',
    icon: '🏠',
    items: [
      { name: 'Properties',        href: '/app/properties',                 icon: '🏠' },
      { name: 'Status Board',      href: '/app/properties/status-board',    icon: '🟢' },
      { name: 'Portfolio Health',  href: '/app/properties/portfolio-health', icon: '📊' },
      { name: 'Property Timeline', href: '/app/properties/timeline',        icon: '🕐' },
      { name: 'Calendar',      href: '/app/calendar',         icon: '📅' },
      { name: 'Vacancy View',  href: '/app/calendar/vacancy', icon: '🟩' },
      { name: 'Heatmap',       href: '/app/calendar/heatmap', icon: '🌡️' },
      { name: 'Channels & email', href: '/app/calendar/sync-status', icon: '🔁' },
      { name: 'Access Codes',     href: '/app/access-codes',              icon: '🔑' },
      { name: 'Lock Analytics',   href: '/app/access-codes/analytics',    icon: '🔐' },
      { name: 'Noise Monitors',  href: '/app/noise',            icon: '🔊' },
      { name: 'Noise Analytics', href: '/app/noise/analytics', icon: '📊' },
      { name: 'Event Log',       href: '/app/noise/events',    icon: '📋' },
      { name: 'Announcements', href: '/app/properties/announcements', icon: '📢' },
      { name: 'Seasonal Notes',href: '/app/properties/seasonal-notes', icon: '📝' },
      { name: 'Documents',     href: '/app/properties/documents',     icon: '📁' },
      { name: 'Min Stay Rules',  href: '/app/properties/min-stay-rules', icon: '📐' },
      { name: 'Bulk Pricing',    href: '/app/properties/bulk-pricing',   icon: '💱' },
      { name: 'STR Permits',   href: '/app/compliance/permits',    icon: '📋' },
      { name: 'Renewals',      href: '/app/compliance/renewals',   icon: '📅' },
      { name: 'Security',      href: '/app/security',              icon: '🔐' },
      { name: 'Audit Log',     href: '/app/security/audit-log',    icon: '🔍' },
    ],
  },
  {
    name: 'Operations',
    icon: '🔄',
    items: [
      { name: 'Work Requests', href: '/app/operations', icon: '📋' },
      { name: 'Turnovers',        href: '/app/turnovers',        icon: '🔄', badgeKey: 'turnovers'     },
      { name: 'Turnover Analytics',  href: '/app/turnovers/analytics',   icon: '📈' },
      { name: 'Turnover Compliance', href: '/app/turnovers/compliance',   icon: '✅' },
      { name: 'Checkout Readiness', href: '/app/checkout/readiness',     icon: '✅' },
      { name: 'Checkout Analytics', href: '/app/checkout/analytics',     icon: '📋' },
      { name: 'Checkout Tasks',    href: '/app/checkout/tasks',          icon: '☑️' },
      { name: 'Cleaning Sessions',href: '/app/cleaning',         icon: '🧹' },
      { name: 'Cleaning Analytics',href: '/app/cleaning/analytics', icon: '📈' },
      { name: 'Workers',          href: '/app/workers',          icon: '👷' },
      { name: 'Work Schedule',    href: '/app/schedule',         icon: '📋' },
      { name: 'Schedule',         href: '/app/workers/schedule', icon: '🗓️' },
      { name: 'Payroll',          href: '/app/workers/payroll',  icon: '$'  },
      { name: 'Labor Analytics',  href: '/app/workers/labor',    icon: '⏱️' },
      { name: 'Worker Ratings',   href: '/app/workers/ratings',  icon: '⭐' },
      { name: 'Callout Tracker',  href: '/app/workers/callouts', icon: '🚨' },
      { name: 'Quality Scores',   href: '/app/workers/quality-scores', icon: '📊' },
    ],
  },
  {
    name: 'Maintenance & Supply',
    icon: '🔧',
    items: [
      { name: 'Repair Requests', href: '/app/repair-requests',            icon: '🔩', badgeKey: 'repairs' },
      { name: 'Cost Analytics',  href: '/app/repair-requests/analytics', icon: '📈'                         },
      { name: 'Maintenance',      href: '/app/maintenance',               icon: '🔧' },
      { name: 'Preventive Tasks', href: '/app/maintenance/preventive',   icon: '🛠️' },
      { name: 'Damage Reports',   href: '/app/maintenance/damage-reports', icon: '🚧' },
      { name: 'Cost Forecast',    href: '/app/maintenance/forecast',     icon: '📊' },
      { name: 'Inspections',          href: '/app/inspection',            icon: '🔍' },
      { name: 'Inspection Analytics', href: '/app/inspection/analytics',  icon: '📈' },
      { name: 'Inventory',          href: '/app/inventory/items',      icon: '📦', badgeKey: 'inventory' },
      { name: 'Catalog',             href: '/app/inventory/catalog',    icon: '📋'                       },
      { name: 'Inventory Analytics', href: '/app/inventory/analytics', icon: '📊'                       },
      { name: 'Suppliers',            href: '/app/inventory/suppliers',  icon: '🏭'                       },
      { name: 'Vendors',              href: '/app/vendors',              icon: '🛠️' },
      { name: 'Vendor Comms',        href: '/app/vendors/comms',        icon: '📝' },
      { name: 'Vendor Performance',  href: '/app/vendors/performance',  icon: '📈' },
      { name: 'Vendor Invoices',     href: '/app/vendors/invoices',     icon: '🧾' },
      { name: 'Supply Requests',          href: '/app/supply-requests',            icon: '🛒', badgeKey: 'supplyRequests' },
      { name: 'Supply Analytics',         href: '/app/supply-requests/analytics',  icon: '📈' },
    ],
  },
  {
    name: 'Finance',
    icon: '💰',
    items: [
      { name: 'Financial',          href: '/app/financial',                        icon: '💰' },
      { name: 'Analytics',          href: '/app/financial/analytics',              icon: '📊' },
      { name: 'Revenue',            href: '/app/financial/revenue',                icon: '💵' },
      { name: 'Revenue Detail',     href: '/app/financial/revenue-detail',          icon: '📋' },
      { name: 'Revenue Pacing',     href: '/app/financial/revenue-pacing',         icon: '🏁' },
      { name: 'Expenses',           href: '/app/financial/expense-tracker',        icon: '🧾' },
      { name: 'Expense Ledger',     href: '/app/financial/expenses',               icon: '🗒️' },
      { name: 'Expense Budgets',    href: '/app/financial/expense-budget',         icon: '🎯' },
      { name: 'P&L',                href: '/app/financial/pnl',                    icon: '📉' },
      { name: 'Trends',             href: '/app/financial/trends',                 icon: '📈' },
      { name: 'Forecast',           href: '/app/financial/forecast',               icon: '🔮' },
      { name: 'Monthly Report',     href: '/app/financial/monthly-report',         icon: '📰' },
      { name: 'Smart Pricing',      href: '/app/financial/smart-pricing',          icon: '🧠' },
      { name: 'Opportunities',      href: '/app/financial/pricing-opportunities',  icon: '💡' },
      { name: 'Property ROI',       href: '/app/financial/property-roi',           icon: '🏠' },
      { name: 'Owner Statement',    href: '/app/financial/owner-statement',        icon: '📄' },
      { name: 'Co-Host Report',     href: '/app/financial/cohost-report',          icon: '🤝' },
      { name: 'Lodging Tax',        href: '/app/financial/lodging-tax',            icon: '🏛️' },
      { name: 'Tax Report',         href: '/app/financial/tax-report',             icon: '🧾' },
      { name: 'Reconciliation',     href: '/app/financial/reconciliation',         icon: '⚖️' },
      { name: 'Revenue Goals',      href: '/app/financial/revenue-goals',          icon: '🎯' },
      { name: 'Channel ROI',        href: '/app/financial/channel-roi',            icon: '📡' },
      { name: 'Channel Mix Trend',  href: '/app/analytics/channel-trend',          icon: '📊' },
      { name: 'Day-of-Week Revenue',href: '/app/analytics/dow-analysis',           icon: '📅' },
      { name: 'Yield Metrics',      href: '/app/analytics/yield-metrics',          icon: '📈' },
      { name: 'Booking Lead Time',  href: '/app/analytics/booking-lead-time',      icon: '⏱️' },
      { name: 'Booking Pace',       href: '/app/analytics/booking-pace',           icon: '📈' },
      { name: 'P&L Trend',          href: '/app/analytics/pnl-trend',              icon: '💰' },
      { name: 'Response Time',      href: '/app/analytics/response-time',          icon: '⚡' },
      { name: 'Revenue Trend',      href: '/app/analytics/revenue-trend',          icon: '📊' },
      { name: 'Ad Spend',           href: '/app/financial/ad-spend',               icon: '📣' },
      { name: 'Competitor Rates',   href: '/app/financial/competitive-analysis',   icon: '🏆' },
      { name: 'Guest Demographics', href: '/app/financial/demographics',           icon: '👥' },
      { name: 'Payout Analytics',   href: '/app/financial/payout-analytics',       icon: '💳' },
      { name: 'Upsell Orders',      href: '/app/financial/upsell-orders',          icon: '🛍️' },
      { name: 'Upsell Analytics',   href: '/app/financial/upsell-analytics',       icon: '📊' },
      { name: 'Upsell Config',      href: '/app/financial/upsell-config',          icon: '⚙️' },
      { name: 'Security Deposits',  href: '/app/financial/security-deposits',      icon: '🔒' },
      { name: 'Pricing History',    href: '/app/financial/pricing-history',        icon: '📈' },
      { name: 'Cashflow',           href: '/app/financial/cashflow',               icon: '💵' },
      { name: 'Recurring Bills',    href: '/app/financial/recurring-expenses',     icon: '🔁' },
      { name: 'Payment Plans',      href: '/app/financial/payment-plans',          icon: '📆' },
      { name: 'Utilities',          href: '/app/financial/utilities',              icon: '⚡' },
      { name: 'Insurance Payouts',  href: '/app/financial/insurance-payouts',      icon: '🛡️' },
      { name: 'Service Prices',     href: '/app/financial/service-prices',         icon: '💲' },
      { name: 'Invoices',           href: '/app/financial/invoices',               icon: '🧾' },
      { name: 'Property Compare',   href: '/app/financial/property-comparison',    icon: '⚖️' },
    ],
  },
  {
    name: 'AI Insights',
    icon: '🤖',
    items: [
      { name: 'AI Insights Hub',   href: '/app/ai-insights',         icon: '🤖' },
    ],
  },
  {
    name: 'City Guide',
    icon: '🗺️',
    items: [
      { name: 'Guidebook Manager', href: '/app/guidebook-manager',   icon: '📖' },
      { name: 'Property Guidebook', href: '/app/guidebook',             icon: '📓' },
      { name: 'Local Events',      href: '/app/city-guide/events',         icon: '📅' },
      { name: 'Guest Saved Places', href: '/app/city-guide/guest-places',  icon: '❤️' },
      { name: 'Co-Owner View',     href: '/app/co-owner/dashboard',  icon: '👥' },
    ],
  },
];

const PRIMARY_NAV_ITEMS: NavItem[] = [
  { name: 'Today',      href: '/app/today',            icon: '☀️', badgeKey: 'actionRequired' },
  { name: 'Inbox',      href: '/app/messages/unified', icon: '📨', badgeKey: 'messages' },
  { name: 'Calendar',   href: '/app/calendar',         icon: '📅' },
  { name: 'Bookings',   href: '/app/bookings',         icon: '🗂️' },
  { name: 'Properties', href: '/app/properties',       icon: '🏠' },
];

/**
 * The sidebar is intentionally a curated set of destinations. Deep and
 * infrequent tools remain available through the command palette.
 */
const NAV_SECTIONS: NavSection[] = [
  {
    name: 'Financial',
    icon: '💰',
    items: [
      { name: 'Finance Overview', href: '/app/financial', icon: '💰' },
      { name: 'Revenue',          href: '/app/financial/revenue', icon: '💵' },
      { name: 'Expenses',         href: '/app/financial/expense-tracker', icon: '🧾' },
      { name: 'Pricing',          href: '/app/financial/smart-pricing', icon: '🧠' },
      { name: 'Reports',          href: '/app/financial/monthly-report', icon: '📰' },
      { name: 'Taxes',            href: '/app/financial/lodging-tax', icon: '🏛️' },
    ],
  },
  {
    name: 'Team & Operations',
    icon: '🔄',
    items: [
      { name: 'Turnovers',   href: '/app/turnovers', icon: '🔄', badgeKey: 'turnovers' },
      { name: 'Cleaning',    href: '/app/cleaning', icon: '🧹' },
      { name: 'Workers',     href: '/app/workers', icon: '👷' },
      { name: 'Maintenance', href: '/app/maintenance', icon: '🔧' },
      { name: 'Repairs',     href: '/app/repair-requests', icon: '🔩', badgeKey: 'repairs' },
      { name: 'Vendors',     href: '/app/vendors', icon: '🛠️' },
      { name: 'Inventory',   href: '/app/inventory/items', icon: '📦', badgeKey: 'inventory' },
    ],
  },
  {
    name: 'Guest Experience',
    icon: '👥',
    items: [
      { name: 'Guests',             href: '/app/guests', icon: '👥' },
      { name: 'Booking Requests',   href: '/app/booking-requests', icon: '📨', badgeKey: 'bookingRequests' },
      { name: 'Guest Requests',     href: '/app/guest-requests', icon: '🕐', badgeKey: 'guestRequests' },
      { name: 'Reviews',            href: '/app/reviews', icon: '⭐' },
      { name: 'Check-in Readiness', href: '/app/checkin-readiness', icon: '✈️' },
      { name: 'Guidebooks',         href: '/app/guidebook-manager', icon: '📖' },
    ],
  },
  {
    name: 'Administration',
    icon: '🔧',
    items: [
      { name: 'Action Required', href: '/app/action-required', icon: '🚨', badgeKey: 'actionRequired' },
      { name: 'Tasks',           href: '/app/tasks', icon: '✓' },
      { name: 'Access Codes',    href: '/app/access-codes', icon: '🔑' },
      { name: 'Permits',         href: '/app/compliance/permits', icon: '📋' },
      { name: 'Security',        href: '/app/security', icon: '🔐' },
      { name: 'Settings',        href: '/app/settings', icon: '🔧' },
    ],
  },
];

// The command palette remains the complete catalog for deep navigation.
const ALL_PALETTE_LINKS: PaletteLink[] = ALL_NAV_SECTIONS.flatMap(section =>
  section.items.map(item => ({
    name: item.name,
    href: item.href,
    section: section.name,
    icon: item.icon,
  }))
);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function findSectionForPath(pathname: string): string | null {
  for (const section of NAV_SECTIONS) {
    if (section.items.some(item => pathname === item.href || pathname.startsWith(item.href + '/'))) {
      return section.name;
    }
  }
  return null;
}

const STORAGE_KEY = 'openbnb_sidebar_sections_v2';

function loadSavedSections(): Record<string, boolean> {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? (JSON.parse(saved) as Record<string, boolean>) : {};
  } catch {
    return {};
  }
}

// ---------------------------------------------------------------------------
// NavSection component
// ---------------------------------------------------------------------------

interface NavSectionProps {
  section: NavSection;
  expanded: boolean;
  onToggle: () => void;
  badges: Record<string, number>;
  onItemClick?: () => void;
}

function CollapsibleSection({ section, expanded, onToggle, badges, onItemClick }: NavSectionProps) {
  return (
    <div>
      {/* Section header */}
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="w-full flex items-center gap-2 px-4 py-2 mt-4 first:mt-2 rounded-lg text-left
          hover:bg-gray-100 dark:hover:bg-gray-700/60 focus:outline-none focus:ring-2
          focus:ring-blue-500 focus:ring-inset transition-colors group"
      >
        <NavIcon icon={section.icon} className="w-4 h-4 shrink-0 text-gray-500 dark:text-gray-400" />
        <span className="flex-1 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-300">
          {section.name}
        </span>
        <ChevronDown
          size={14}
          className={`text-gray-400 dark:text-gray-500 transition-transform duration-200 ${expanded ? 'rotate-0' : '-rotate-90'}`}
          aria-hidden="true"
        />
      </button>

      {/* Items */}
      <div
        className={`overflow-hidden transition-all duration-200 ${expanded ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'}`}
        aria-hidden={!expanded}
      >
        <div className="space-y-0.5 pb-1">
          {section.items.map(item => {
            const badgeCount = item.badgeKey ? (badges[item.badgeKey] ?? 0) : 0;
            return (
              <NavLink
                key={item.href}
                to={item.href}
                end={item.href !== '/app'}
                onClick={onItemClick}
                tabIndex={expanded ? 0 : -1}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-2.5 ml-1 rounded-lg text-sm font-medium transition-colors
                  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset ${
                    isActive
                      ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`
                }
              >
                <NavIcon icon={item.icon} className="w-4 h-4 shrink-0" />
                <span className="flex-1 leading-tight">{item.name}</span>
                {badgeCount > 0 && (
                  <span
                    className="text-xs font-bold px-1.5 py-0.5 rounded-full bg-orange-500 text-white min-w-[1.25rem] text-center shrink-0"
                    aria-label={`${badgeCount} alert${badgeCount !== 1 ? 's' : ''}`}
                  >
                    <span aria-hidden="true">{badgeCount}</span>
                  </span>
                )}
              </NavLink>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// NavItems — full grouped navigation with command palette trigger
// ---------------------------------------------------------------------------

interface NavItemsProps {
  badges: Record<string, number>;
  onItemClick?: () => void;
}

function NavItems({ badges, onItemClick }: NavItemsProps) {
  const location = useLocation();
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>(loadSavedSections);
  const [paletteOpen, setPaletteOpen] = useState(false);

  // Auto-expand the section that contains the current route
  useEffect(() => {
    const activeSectionName = findSectionForPath(location.pathname);
    if (activeSectionName) {
      setExpandedSections(prev => {
        if (prev[activeSectionName]) return prev; // already expanded, no re-render
        const next = { ...prev, [activeSectionName]: true };
        try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
        return next;
      });
    }
  }, [location.pathname]);

  // Persist toggle to localStorage
  const toggleSection = useCallback((name: string) => {
    setExpandedSections(prev => {
      const next = { ...prev, [name]: !prev[name] };
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  }, []);

  // Global ⌘K / Ctrl+K shortcut
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setPaletteOpen(open => !open);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  return (
    <>
      <nav aria-label="Main navigation" className="px-3 py-4">
        <div className="space-y-0.5">
          <p className="px-4 pb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Daily
          </p>
          {PRIMARY_NAV_ITEMS.map(item => {
            const badgeCount = item.badgeKey ? (badges[item.badgeKey] ?? 0) : 0;
            return (
              <NavLink
                key={item.href}
                to={item.href}
                onClick={onItemClick}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors
                  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset ${
                    isActive
                      ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`
                }
              >
                <NavIcon icon={item.icon} className="w-4 h-4 shrink-0" />
                <span className="flex-1 leading-tight">{item.name}</span>
                {badgeCount > 0 && (
                  <span
                    className="text-xs font-bold px-1.5 py-0.5 rounded-full bg-orange-500 text-white min-w-[1.25rem] text-center shrink-0"
                    aria-label={`${badgeCount} alert${badgeCount !== 1 ? 's' : ''}`}
                  >
                    <span aria-hidden="true">{badgeCount}</span>
                  </span>
                )}
              </NavLink>
            );
          })}
        </div>
        {NAV_SECTIONS.map(section => (
          <CollapsibleSection
            key={section.name}
            section={section}
            expanded={!!expandedSections[section.name]}
            onToggle={() => toggleSection(section.name)}
            badges={badges}
            onItemClick={onItemClick}
          />
        ))}

        {/* ⌘K hint */}
        <div className="mt-6 px-1">
          <button
            type="button"
            onClick={() => setPaletteOpen(true)}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-gray-400 dark:text-gray-500
              border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600
              hover:text-gray-600 dark:hover:text-gray-400 transition-colors focus:outline-none
              focus:ring-2 focus:ring-blue-500 focus:ring-inset"
            aria-label="Open command palette (⌘K)"
          >
            <span aria-hidden="true">🔍</span>
            <span className="flex-1 text-left">Jump to...</span>
            <kbd className="font-mono text-xs bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded border border-gray-200 dark:border-gray-600">
              ⌘K
            </kbd>
          </button>
        </div>
      </nav>

      <CommandPalette
        isOpen={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        links={ALL_PALETTE_LINKS}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Sidebar (desktop + mobile drawer)
// ---------------------------------------------------------------------------

interface SidebarProps {
  open?: boolean;
  onClose?: () => void;
}

export function Sidebar({ open, onClose }: SidebarProps) {
  const [badges, setBadges] = useState<Record<string, number>>({});

  const loadBadges = useCallback(() => {
    const headers = { credentials: 'include' as const };

    fetch('/api/inventory/items?low_stock=true&limit=1', headers)
      .then(r => r.ok ? r.json() : { low_stock_count: 0 })
      .then((d: { low_stock_count?: number }) => {
        setBadges(prev => ({ ...prev, inventory: d.low_stock_count ?? 0 }));
      })
      .catch(() => {});

    fetch('/api/booking-requests?status=pending&count=true', headers)
      .then(r => r.ok ? r.json() : { total: 0 })
      .then((d: { total?: number }) => {
        setBadges(prev => ({ ...prev, bookingRequests: d.total ?? 0 }));
      })
      .catch(() => {});

    fetch('/api/owner/unread-messages', headers)
      .then(r => r.ok ? r.json() : { threads: [], ota_unanswered: 0 })
      .then((d: { threads?: unknown[]; direct_unanswered?: number; ota_unanswered?: number }) => {
        setBadges(prev => ({ ...prev, messages: (d.direct_unanswered ?? d.threads?.length ?? 0) + (d.ota_unanswered ?? 0) }));
      })
      .catch(() => {});

    fetch('/api/owner/guest-conversations', headers)
      .then(r => r.ok ? r.json() : { total_unread: 0 })
      .then((d: { total_unread?: number }) => {
        setBadges(prev => ({ ...prev, guestConvs: d.total_unread ?? 0 }));
      })
      .catch(() => {});

    fetch('/api/owner/activity?limit=20', headers)
      .then(r => r.ok ? r.json() : { items: [] })
      .then((d: { items?: Array<{ created_at: string }> }) => {
        const lastViewed = localStorage.getItem('openbnb_activity_last_viewed') ?? '1970-01-01T00:00:00Z';
        const count = (d.items ?? []).filter(i => new Date(i.created_at) > new Date(lastViewed)).length;
        setBadges(prev => ({ ...prev, activity: count }));
      })
      .catch(() => {});

    fetch('/api/repair-requests?status=pending', headers)
      .then(r => r.ok ? r.json() : null)
      .then((d: { requests?: unknown[] } | null) => {
        setBadges(prev => ({ ...prev, repairs: d?.requests?.length ?? 0 }));
      })
      .catch(() => {});

    fetch('/api/supply-requests?status=pending', headers)
      .then(r => r.ok ? r.json() : { pending_count: 0 })
      .then((d: { pending_count?: number }) => {
        setBadges(prev => ({ ...prev, supplyRequests: d.pending_count ?? 0 }));
      })
      .catch(() => {});

    fetch('/api/owner/special-requests?status=pending', headers)
      .then(r => r.ok ? r.json() : { pending_count: 0 })
      .then((d: { pending_count?: number }) => {
        setBadges(prev => ({ ...prev, guestRequests: d.pending_count ?? 0 }));
      })
      .catch(() => {});

    fetch('/api/owner/inquiries?status=new&page=1', headers)
      .then(r => r.ok ? r.json() : { new_count: 0 })
      .then((d: { new_count?: number }) => {
        setBadges(prev => ({ ...prev, inquiries: d.new_count ?? 0 }));
      })
      .catch(() => {});

    fetch('/api/turnovers?status=pending&limit=1', headers)
      .then(r => r.ok ? r.json() : null)
      .then((d: { turnovers?: unknown[]; total?: number } | null) => {
        setBadges(prev => ({ ...prev, turnovers: d?.total ?? d?.turnovers?.length ?? 0 }));
      })
      .catch(() => {});

    fetch('/api/owner/action-required', headers)
      .then(r => r.ok ? r.json() : { critical: 0 })
      .then((d: { critical?: number }) => {
        setBadges(prev => ({ ...prev, actionRequired: d.critical ?? 0 }));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadBadges();
    const interval = setInterval(loadBadges, 60 * 1000);
    const onVisible = () => { if (document.visibilityState === 'visible') loadBadges(); };
    document.addEventListener('visibilitychange', onVisible);
    document.addEventListener('refreshSidebarBadges', loadBadges);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      document.removeEventListener('refreshSidebarBadges', loadBadges);
    };
  }, [loadBadges]);

  return (
    <>
      {/* Desktop sidebar — always visible on lg+ */}
      <aside className="hidden lg:block w-64 bg-gray-50 dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 min-h-[calc(100vh-4rem)] shrink-0 overflow-y-auto">
        <NavItems badges={badges} />
      </aside>

      {/* Mobile drawer — shown when open=true */}
      {open && (
        <>
          {/* Backdrop */}
          <div
            className="lg:hidden fixed inset-0 z-40 bg-black/40"
            onClick={onClose}
            aria-hidden="true"
          />
          {/* Drawer */}
          <aside
            className="lg:hidden fixed top-0 left-0 bottom-0 z-50 w-72 bg-white dark:bg-gray-800 shadow-xl overflow-y-auto"
            role="dialog"
            aria-modal="true"
            aria-label="Navigation menu"
          >
            <div className="flex items-center justify-between px-4 h-16 border-b border-gray-200 dark:border-gray-700">
              <span className="text-lg font-bold text-blue-600">OpenBNB</span>
              <button
                onClick={onClose}
                className="text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white text-2xl leading-none focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                aria-label="Close navigation menu"
              >
                <span aria-hidden="true">×</span>
              </button>
            </div>
            <NavItems badges={badges} onItemClick={onClose} />
          </aside>
        </>
      )}
    </>
  );
}
